import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Button, SimpleLoading, Text } from "@core/index";
import { useGetAssignment } from "@packages/api/madlenAPI";
import ErrorComponent from "@features/tools/components/error-component";
import {
  ArrowLeft,
  Link2,
  Copy,
  ChevronDown,
  ChevronRight,
} from "lucide-react";

type Item = { id: string; text: string } | string;

const PronunciationAssessmentDetailView = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [, setCopying] = useState(false);

  const { data, isLoading, error } = useGetAssignment(Number(id), {
    query: {
      enabled: !!id,
      refetchInterval: 30000,
      refetchOnWindowFocus: false,
    },
  });

  const items = useMemo(() => {
    const raw: any = (data as any)?.items;
    if (!raw || !Array.isArray(raw))
      return [] as { id: string; text: string }[];
    return raw
      .map((it: Item, idx: number) => {
        if (typeof it === "string") return { id: String(idx + 1), text: it };
        if (it && typeof (it as any).text === "string" && (it as any).id)
          return { id: String((it as any).id), text: (it as any).text };
        if (it && typeof (it as any).text === "string")
          return { id: String(idx + 1), text: (it as any).text };
        return null;
      })
      .filter(Boolean) as { id: string; text: string }[];
  }, [data]);

  const itemsById = useMemo(() => {
    const map: Record<string, { id: string; text: string; index: number }> = {};
    items.forEach((it, idx) => {
      map[it.id] = { ...it, index: idx + 1 };
    });
    return map;
  }, [items]);

  const studentSummaries = useMemo(() => {
    const submissions = (data as any)?.submissions as any[] | undefined;
    if (!Array.isArray(submissions) || submissions.length === 0)
      return [] as any[];

    const grouped: Record<string, { resultsByItemId: Record<string, any[]> }> =
      {};
    for (const sub of submissions) {
      const solverName = (sub?.solverName || "-") as string;
      if (!grouped[solverName]) grouped[solverName] = { resultsByItemId: {} };
      const resMap = sub?.results || {};
      for (const key of Object.keys(resMap)) {
        const arr = Array.isArray(resMap[key]) ? resMap[key] : [];
        if (!grouped[solverName].resultsByItemId[key])
          grouped[solverName].resultsByItemId[key] = [];
        grouped[solverName].resultsByItemId[key] = [
          ...grouped[solverName].resultsByItemId[key],
          ...arr,
        ];
      }
    }

    const summaries = Object.entries(grouped).map(([name, g]) => {
      const lastResultsByItemId: Record<string, any> = {};
      Object.entries(g.resultsByItemId).forEach(([itemId, attempts]) => {
        if (Array.isArray(attempts) && attempts.length > 0) {
          lastResultsByItemId[itemId] = attempts[attempts.length - 1]?.result;
        }
      });
      const allOveralls = Object.values(lastResultsByItemId)
        .map((r: any) => (typeof r?.overall === "number" ? r.overall : null))
        .filter((n: any) => typeof n === "number") as number[];
      const avgOverall = allOveralls.length
        ? Math.round(
            (allOveralls.reduce((a, b) => a + b, 0) / allOveralls.length) * 10,
          ) / 10
        : null;
      const completedCount = Object.keys(lastResultsByItemId).length;
      return { name, lastResultsByItemId, avgOverall, completedCount };
    });

    // Sort by avg desc then name
    summaries.sort((a, b) => {
      if (
        typeof b.avgOverall === "number" &&
        typeof a.avgOverall === "number"
      ) {
        if (b.avgOverall !== a.avgOverall) return b.avgOverall - a.avgOverall;
      } else if (typeof b.avgOverall === "number") return 1;
      else if (typeof a.avgOverall === "number") return -1;
      return String(a.name).localeCompare(String(b.name));
    });
    return summaries;
  }, [data]);

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const toggleExpand = (name: string) =>
    setExpanded((prev) => ({ ...prev, [name]: !prev[name] }));
  const [expandedWords, setExpandedWords] = useState<Record<string, boolean>>(
    {},
  );
  const toggleWords = (key: string) =>
    setExpandedWords((prev) => ({ ...prev, [key]: !prev[key] }));

  function scoreColor(value?: number | null): string {
    if (typeof value !== "number") return "#9CA3AF"; // gray-400
    if (value < 60) return "#EF4444"; // red-500
    if (value < 80) return "#F59E0B"; // amber-500
    return "#10B981"; // emerald-500
  }

  function OverallRing({
    value,
    size = 40,
    track = "#E5E7EB",
  }: {
    value?: number | null;
    size?: number;
    track?: string;
  }) {
    const normalized = Math.max(
      0,
      Math.min(100, typeof value === "number" ? value : 0),
    );
    const color = scoreColor(value ?? null);
    const bg = `conic-gradient(${color} ${normalized * 3.6}deg, ${track} 0deg)`;
    return (
      <div
        className="relative flex items-center justify-center rounded-full"
        style={{ width: size, height: size, backgroundImage: bg }}
        title={`${t("pronunciationPublic.overall")}: ${typeof value === "number" ? value : "-"}`}
        aria-label={`${t("pronunciationPublic.overall")}: ${typeof value === "number" ? value : "-"}`}
      >
        <div className="absolute inset-1 rounded-full bg-white dark:bg-dark-600" />
        <div className="relative text-xs font-semibold" style={{ color }}>
          {typeof value === "number" ? Math.round(value) : "-"}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <SimpleLoading fastLoading />;
  }

  if (error || !data) {
    return (
      <ErrorComponent
        title={t("common.error")}
        description={t("common.error")}
      />
    );
  }

  const title = (data as any)?.title || "";
  const description = (data as any)?.description || "";
  const createdAt = (data as any)?.createdAt;
  const publicCode = (data as any)?.publicCode;
  const publicUrl = publicCode
    ? `${window.location.origin}/pronunciation-student/v2/${publicCode}`
    : "";

  const formatRelativeTime = (input?: string | number | Date) => {
    if (!input) return "";
    const date = new Date(input);
    if (isNaN(date.getTime())) return "";
    const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000);
    const rtf = new Intl.RelativeTimeFormat(i18n.language, { numeric: "auto" });
    const units: [Intl.RelativeTimeFormatUnit, number][] = [
      ["year", 60 * 60 * 24 * 365],
      ["month", 60 * 60 * 24 * 30],
      ["week", 60 * 60 * 24 * 7],
      ["day", 60 * 60 * 24],
      ["hour", 60 * 60],
      ["minute", 60],
      ["second", 1],
    ];
    for (const [unit, sec] of units) {
      if (Math.abs(diffSeconds) >= sec || unit === "second") {
        const value = Math.round(diffSeconds / sec);
        return rtf.format(value, unit);
      }
    }
    return "";
  };

  return (
    <div className="flex-1 p-4 bg-gray-50 dark:bg-dark-500 overflow-y-auto h-full">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <Button
            onClick={() => navigate("/content/pronunciation-assessment")}
            size="md"
            rounded="full"
            variant="outline"
            leftIcon={<ArrowLeft size={18} />}
          >
            {t("common.back")}
          </Button>
        </div>

        <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-6 mb-4">
          <Text variant="h1" className="text-2xl font-semibold mb-2">
            {title && String(title).trim().length > 0
              ? title
              : t("pronunciationAssessment.create.basicInfo.titlePlaceholder")}
          </Text>
          {description ? (
            <Text variant="p" className="text-gray-600 dark:text-gray-300">
              {description}
            </Text>
          ) : null}
          {createdAt && (
            <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
              {formatRelativeTime(createdAt)}
            </div>
          )}
        </div>

        {publicUrl && (
          <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-6 mb-4">
            <Text variant="h2" className="text-lg font-semibold mb-3">
              {t("pronunciationAssessment.create.share.title")}
            </Text>
            <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-dark-500 border border-gray-200 dark:border-dark-400">
              <div className="w-9 h-9 rounded-lg bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 flex items-center justify-center">
                <Link2 className="w-4 h-4" />
              </div>
              <input
                type="text"
                readOnly
                value={publicUrl}
                className="flex-1 px-3 py-2 border rounded-lg focus:outline-none dark:bg-dark-500 dark:border-dark-400 dark:text-white border-gray-300"
              />
              <Button
                size="sm"
                variant="outline"
                leftIcon={<Copy className="w-4 h-4" />}
                onClick={async () => {
                  try {
                    setCopying(true);
                    await navigator.clipboard.writeText(publicUrl);
                  } finally {
                    setCopying(false);
                  }
                }}
              >
                {t("common.copy")}
              </Button>
              <Button
                size="sm"
                variant="outline"
                leftIcon={<Link2 className="w-4 h-4" />}
                onClick={() => window.open(publicUrl, "_blank")}
              >
                Open
              </Button>
            </div>
          </div>
        )}

        <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-6">
          <Text variant="h2" className="text-lg font-semibold mb-4">
            {t("pronunciationAssessment.title")}
          </Text>
          {items.length === 0 ? (
            <Text variant="p" className="text-gray-600 dark:text-gray-400">
              —
            </Text>
          ) : (
            <div className="space-y-3">
              {items.map((it, idx) => (
                <div
                  key={it.id}
                  className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-dark-500 border border-gray-200 dark:border-dark-400"
                >
                  <div className="w-8 h-8 rounded-full bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 flex items-center justify-center text-sm font-semibold">
                    {idx + 1}
                  </div>
                  <div className="flex-1 text-gray-900 dark:text-gray-100">
                    {it.text}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Student submissions */}
        <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-6 mt-4">
          <Text variant="h2" className="text-lg font-semibold mb-4">
            {t("pronunciationAssessment.detail.submissionsTitle")}
          </Text>
          {studentSummaries.length === 0 ? (
            <Text variant="p" className="text-gray-600 dark:text-gray-400">
              {t("pronunciationAssessment.detail.noSubmissions")}
            </Text>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-dark-400">
              {studentSummaries.map((stu) => {
                const isOpen = !!expanded[stu.name];
                return (
                  <div key={stu.name} className="py-3">
                    <button
                      type="button"
                      onClick={() => toggleExpand(stu.name)}
                      className="w-full grid grid-cols-[auto,1fr,auto] items-center gap-3 text-left"
                    >
                      <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 flex items-center justify-center text-sm font-semibold">
                        {String(stu.name || "?")
                          .substring(0, 1)
                          .toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                          {stu.name}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          {t("pronunciationAssessment.detail.completed")}{" "}
                          {stu.completedCount || 0}/{items.length}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <OverallRing value={stu.avgOverall} size={32} />
                          <span className="hidden sm:inline text-[11px] sm:text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">
                            {t("pronunciationAssessment.detail.average")}{" "}
                            {t("pronunciationPublic.overall")}
                          </span>
                        </div>
                        {isOpen ? (
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-500" />
                        )}
                      </div>
                    </button>

                    {isOpen && (
                      <div className="mt-3 pl-11 space-y-3">
                        {Object.entries(stu.lastResultsByItemId).map(
                          ([itemId, result]: any) => {
                            const meta = itemsById[itemId];
                            if (!result || !meta) return null;
                            const metricChip = (
                              label: string,
                              value: string | number | undefined,
                              color = "bg-gray-100 text-gray-700 dark:bg-dark-500 dark:text-gray-200",
                            ) => (
                              <div
                                className={`px-2 py-1 rounded-md text-xs font-medium ${color}`}
                              >
                                <span className="opacity-70 mr-1">
                                  {label}:
                                </span>
                                <span>{value ?? "-"}</span>
                              </div>
                            );
                            return (
                              <div
                                key={itemId}
                                className="border rounded-lg p-3 dark:border-dark-400"
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex items-start gap-3">
                                    <div className="w-7 h-7 rounded-full bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 flex items-center justify-center text-xs font-semibold mt-0.5">
                                      {meta.index}
                                    </div>
                                    <div className="text-gray-900 dark:text-gray-100">
                                      {meta.text}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3 flex-wrap">
                                    <div className="flex items-center gap-1">
                                      <OverallRing
                                        value={result.overall}
                                        size={28}
                                      />
                                      <span className="text-[11px] sm:text-xs text-gray-600 dark:text-gray-300">
                                        {t("pronunciationPublic.overall")}
                                      </span>
                                    </div>
                                    {metricChip(
                                      t("pronunciationPublic.pronunciation"),
                                      result.pronunciation,
                                      "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300",
                                    )}
                                    {metricChip(
                                      t("pronunciationPublic.fluency"),
                                      result.fluency,
                                      "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300",
                                    )}
                                    {metricChip(
                                      t("pronunciationPublic.rhythm"),
                                      result.rhythm,
                                    )}
                                    {metricChip(
                                      t("pronunciationPublic.speed"),
                                      result.speed,
                                    )}
                                    {metricChip(
                                      t("pronunciationPublic.duration"),
                                      result.duration ||
                                        result.numeric_duration,
                                    )}
                                  </div>
                                </div>

                                {Array.isArray(result.words) &&
                                  result.words.length > 0 && (
                                    <div className="mt-3">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          toggleWords(`${stu.name}::${itemId}`)
                                        }
                                        className="px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700 dark:bg-dark-500 dark:text-gray-200 inline-flex items-center gap-1"
                                      >
                                        <span>
                                          {t("pronunciationPublic.words")}
                                        </span>
                                        <span className="opacity-70">
                                          ({result.words.length})
                                        </span>
                                        {expandedWords[
                                          `${stu.name}::${itemId}`
                                        ] ? (
                                          <ChevronDown className="w-3.5 h-3.5" />
                                        ) : (
                                          <ChevronRight className="w-3.5 h-3.5" />
                                        )}
                                      </button>
                                      {expandedWords[
                                        `${stu.name}::${itemId}`
                                      ] && (
                                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                          {result.words.map(
                                            (w: any, wi: number) => (
                                              <div
                                                key={`${w.word}-${wi}`}
                                                className="border rounded-md px-3 py-2 dark:border-dark-400"
                                              >
                                                <div className="flex items-center justify-between">
                                                  <div className="font-medium text-gray-900 dark:text-gray-100">
                                                    {w.word}
                                                  </div>
                                                  <div className="flex items-center gap-2">
                                                    {metricChip(
                                                      t(
                                                        "pronunciationPublic.score",
                                                      ),
                                                      w.scores?.overall ??
                                                        w.scores?.pronunciation,
                                                    )}
                                                    {typeof w.scores
                                                      ?.pronunciation ===
                                                      "number" &&
                                                      metricChip(
                                                        t(
                                                          "pronunciationPublic.pronunciation",
                                                        ),
                                                        w.scores?.pronunciation,
                                                      )}
                                                  </div>
                                                </div>
                                                {Array.isArray(w.phonemes) &&
                                                  w.phonemes.length > 0 && (
                                                    <div className="mt-2 flex flex-wrap gap-1">
                                                      {w.phonemes.map(
                                                        (
                                                          p: any,
                                                          pi: number,
                                                        ) => (
                                                          <span
                                                            key={pi}
                                                            className="px-2 py-0.5 rounded-md text-xs bg-gray-100 text-gray-800 dark:bg-dark-500 dark:text-gray-200"
                                                          >
                                                            /{p.phoneme}/{" "}
                                                            {typeof p.pronunciation ===
                                                            "number"
                                                              ? `(${p.pronunciation})`
                                                              : ""}
                                                          </span>
                                                        ),
                                                      )}
                                                    </div>
                                                  )}
                                              </div>
                                            ),
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}
                              </div>
                            );
                          },
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PronunciationAssessmentDetailView;
